import React, { useState, useEffect, useCallback } from 'react'
import { Outlet, useParams } from 'react-router-dom'
import { useLocalStorage } from 'react-use'

import { getWindow } from '@ztechapps/common.utils'
import { useHistory } from '@ztechapps/common.hook'

import { ShowIf } from '../ShowIf'
import { Icon } from '../Icon'

import styles from './RouteOffCanvas.module.scss'

type RouteOffCanvasProps = {
  children: React.ReactNode
  listRoutePath: string
  defaultWidth?: string
  widthStorageKey?: string
}

export const RouteOffCanvas = ({ children, listRoutePath, defaultWidth = '90%', widthStorageKey }: RouteOffCanvasProps) => {
  const history = useHistory()
  const { _id } = useParams()
  const [show, setShow] = useState(!!_id)
  const storageKey = widthStorageKey ?? `${listRoutePath}-offcanvas-width`
  const [panelWidth, setPanelWidth] = useLocalStorage<string>(storageKey, defaultWidth)
  const [isResizing, setIsResizing] = useState(false)

  useEffect(() => {
    setShow(!!_id)
  }, [_id])

  useEffect(() => {
    const doc = getWindow()?.document
    if (!doc) return

    if (show) {
      doc.body.style.overflow = 'hidden'
    } else {
      doc.body.style.overflow = ''
    }

    return () => {
      doc.body.style.overflow = ''
    }
  }, [show])

  useEffect(() => {
    const win = getWindow()
    const doc = win?.document
    if (!doc) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return

      const windowWidth = win?.innerWidth ?? 1920
      const newWidth = windowWidth - e.clientX
      const widthPercent = (newWidth / windowWidth) * 100

      // Constrain between 10% and 95%
      const constrainedWidth = Math.min(Math.max(widthPercent, 10), 95)
      setPanelWidth(`${constrainedWidth}%`)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      doc.body.style.cursor = ''
      doc.body.style.userSelect = ''
    }

    if (isResizing) {
      doc.body.style.cursor = 'ew-resize'
      doc.body.style.userSelect = 'none'
      doc.addEventListener('mousemove', handleMouseMove)
      doc.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      doc.removeEventListener('mousemove', handleMouseMove)
      doc.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing])

  const handleClose = useCallback(() => {
    history.push(listRoutePath)
    setShow(false)
  }, [history, listRoutePath])

  const handleResizeStart = useCallback(() => {
    setIsResizing(true)
  }, [])

  return (
    <div
      className={`${styles.offcanvasLayout} d-flex w-100 min-vh-100 ${show ? styles.offcanvasOpen : ''}`}
      style={{ '--offcanvas-width': panelWidth } as React.CSSProperties}
    >
      <div className={`${styles.offcanvasMainContent} w-100 overflow-x-auto bg-body text-body`}>{children}</div>

      <ShowIf condition={show}>
        <button
          type="button"
          className={`${styles.closeButton} btn btn-primary btn-sm rounded-circle shadow d-flex align-items-center justify-content-center`}
          style={{ top: '0.5rem', zIndex: 11, width: '2rem', height: '2rem' }}
          onClick={handleClose}
          title="Close"
        >
          <Icon name="times" />
        </button>
      </ShowIf>

      <div
        className={`${styles.offcanvasPanel} vh-100 overflow-y-auto bg-body text-body border-start shadow-lg`}
        style={{ zIndex: 10 }}
      >
        <div
          className={`${styles.resizeHandle} ${isResizing ? styles.resizing : ''}`}
          onMouseDown={handleResizeStart}
          title="Drag to resize"
        />
        <div className="p-3">
          <Outlet context={{ noTopMargin: true, fromOffCanvas: true }} />
        </div>
      </div>
    </div>
  )
}
